import { Box as MuiBox } from "@mui/material";
import { Duplicate } from "@procore/core-icons";
import { Box, Button, colors, Link, spacing, Tooltip, Typography } from "@procore/core-react";
import type { ReactNode } from "react";

import { type ActionStatus, useActionStatus } from "./useActionStatus";

/** @param props.action Rendered at the end of the title row. */
export function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
    return (
        <Box style={{ display: "flex", flexDirection: "column", gap: spacing.sm }}>
            <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 24 }}>
                <Typography intent="label">{title}</Typography>
                {action}
            </Box>
            {children}
        </Box>
    );
}

/** Greyed-out text while `href` is unknown. */
export function ExternalLink({ href, children }: { href: string | undefined; children: ReactNode }) {
    return (
        <Typography intent="small">
            {href ? (
                <Link href={href} target="_blank" rel="noopener noreferrer">
                    {children}
                </Link>
            ) : (
                <span style={{ color: colors.gray45 }}>{children}</span>
            )}
        </Typography>
    );
}

/**
 * @param props.placeholder Shown instead of a missing `value`. Defaults to `"—"`.
 * @param props.children Extra actions after the copy button.
 */
export function IdRow({
    label,
    value,
    placeholder = "—",
    children,
}: {
    label: string;
    value: string | undefined;
    placeholder?: string;
    children?: ReactNode;
}) {
    return (
        <MuiBox
            sx={{
                display: "flex",
                gap: `${spacing.sm}px`,
                alignItems: "center",
                minHeight: 24,
                "& .id-row-actions": { opacity: 0 },
                "&:hover .id-row-actions, &:focus-within .id-row-actions": { opacity: 0.5 },
            }}
        >
            <Typography intent="small" style={{ width: 112, flexShrink: 0 }}>
                {label}
            </Typography>
            <Typography
                intent="small"
                style={{ flex: 1, fontFamily: "monospace", userSelect: "text", wordBreak: "break-all" }}
            >
                {value ?? placeholder}
            </Typography>
            <Box className="id-row-actions" style={{ display: "flex" }}>
                <CopyButton label={label} disabled={value === undefined} getText={() => value ?? ""} />
                {children}
            </Box>
        </MuiBox>
    );
}

/** Flashes the outcome of an action over its button, so the button itself keeps its size. */
/** @param props.error Shown on failure instead of `"Failed"`; required so a caller can't drop it. */
export function StatusTooltip({
    status,
    doneText,
    error,
    children,
}: {
    status: ActionStatus;
    doneText: string;
    error: string | undefined;
    children: ReactNode;
}) {
    const settled = status === "done" || status === "failed";
    return (
        <Tooltip
            trigger={settled ? "always" : "none"}
            overlay={status === "failed" ? (error ?? "Failed") : doneText}
            placement="top"
        >
            {children}
        </Tooltip>
    );
}

/**
 * Copies to the clipboard and shows progress, then success or failure, in place.
 * @param props.getText Text to copy; may resolve asynchronously.
 * @param props.label What is copied, for the accessible name and the error log.
 * @param props.doneText Tooltip shown on success. Defaults to `"Copied"`.
 * @param props.children Button text. Renders an icon button when omitted.
 */
export function CopyButton({
    getText,
    label,
    doneText = "Copied",
    disabled,
    children,
}: {
    getText: () => string | Promise<string>;
    label: string;
    doneText?: string;
    disabled?: boolean;
    children?: ReactNode;
}) {
    const [status, run, error] = useActionStatus();

    const copy = () =>
        run(() => {
            const text = Promise.resolve(getText());
            // A promise-valued `ClipboardItem` keeps the click's user activation across the
            // await, which Safari requires; `writeText` after the await would be rejected.
            return navigator.clipboard.write([
                new ClipboardItem({ "text/plain": text.then((t) => new Blob([t], { type: "text/plain" })) }),
            ]);
        }, `Failed to copy ${label}`);

    const common = {
        size: "sm" as const,
        disabled,
        loading: status === "running",
        onClick: () => void copy(),
    };

    return (
        <StatusTooltip status={status} doneText={doneText} error={error}>
            {children ? (
                <Button {...common} variant="secondary">
                    {children}
                </Button>
            ) : (
                <Button {...common} icon={<Duplicate />} variant="tertiary" aria-label={`Copy ${label}`} />
            )}
        </StatusTooltip>
    );
}

/**
 * Reads the clipboard and hands the text to `apply`, showing progress, then success or failure.
 * @param props.label What is pasted, for the button text and the error log.
 */
export function PasteButton({
    label,
    apply,
    disabled,
}: {
    label: string;
    apply: (text: string) => unknown;
    disabled?: boolean;
}) {
    const [status, run, error] = useActionStatus();

    return (
        <StatusTooltip status={status} doneText="Applied" error={error}>
            <Button
                size="sm"
                variant="secondary"
                disabled={disabled}
                loading={status === "running"}
                onClick={() =>
                    void run(async () => apply(await navigator.clipboard.readText()), `Failed to paste ${label}`)
                }
            >
                Paste {label}
            </Button>
        </StatusTooltip>
    );
}
